import { logEvent, setUserProperties } from "firebase/analytics";
import { analyticsReadyPromise } from "../firebase";

/*
 * Analytics core.
 *
 * Everything here is a no-op when Firebase is unconfigured (see ../firebase),
 * so the site runs identically without env keys — nothing throws, nothing
 * blocks rendering.
 *
 * Three layers of identity, which is what makes retention answerable:
 *
 *   visitor_id  persistent, never expires. "Is this the same person as before?"
 *   session_id  rotates after SESSION_TIMEOUT_MS of inactivity, so a person who
 *               comes back tomorrow starts a NEW session but keeps their
 *               visitor_id. The previous implementation stored one id in
 *               localStorage forever and called it a session, which meant
 *               session counts and session age were both meaningless.
 *   visit_count how many sessions this visitor has started.
 *
 * Every event carries that context, so any event can be split by new vs
 * returning without a separate lookup.
 */

const VISITOR_ID_KEY = "appinfigo_visitor_id";
const FIRST_SEEN_KEY = "appinfigo_first_seen_at";
const VISIT_COUNT_KEY = "appinfigo_visit_count";
const SESSION_KEY = "appinfigo_session_id";
const SESSION_STARTED_AT_KEY = "appinfigo_session_started_at";
const SESSION_LAST_SEEN_KEY = "appinfigo_session_last_seen_at";

/* GA4's own session window, so our numbers line up with the console's. */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const ANALYTICS_READY_TIMEOUT_MS = 2000;
const DAY_MS = 24 * 60 * 60 * 1000;

/* GA4 limits: <=40 chars for names, <=100 for values, <=25 params per event. */
const MAX_PARAM_VALUE_LENGTH = 100;
const MAX_PARAMS = 25;

let analyticsInstance = null;
let identityCache = null;

/* ---------------------------------------------------------------- storage --
   localStorage throws in Safari private mode and when a browser blocks site
   data. Analytics must never take the page down with it, so every read and
   write is guarded and falls back to in-memory values for the tab. */
const memoryStore = {};

function readStore(key) {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? memoryStore[key] : value;
  } catch (error) {
    return memoryStore[key];
  }
}

function writeStore(key, value) {
  memoryStore[key] = value;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* memory-only for this tab; nothing else to do */
  }
}

function randomId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/* --------------------------------------------------------------- identity --
   Resolves visitor + session in one pass and caches for the tab. Call
   touchSession() on activity to keep the session alive. */
function resolveIdentity() {
  const now = Date.now();

  let visitorId = readStore(VISITOR_ID_KEY);
  let firstSeenAt = Number(readStore(FIRST_SEEN_KEY)) || 0;
  const isNewVisitor = !visitorId;

  if (!visitorId) {
    visitorId = randomId();
    firstSeenAt = now;
    writeStore(VISITOR_ID_KEY, visitorId);
    writeStore(FIRST_SEEN_KEY, String(now));
  }

  const lastSeenAt = Number(readStore(SESSION_LAST_SEEN_KEY)) || 0;
  const expired = !lastSeenAt || now - lastSeenAt > SESSION_TIMEOUT_MS;

  let sessionId = readStore(SESSION_KEY);
  let sessionStartedAt = Number(readStore(SESSION_STARTED_AT_KEY)) || 0;
  let visitCount = Number(readStore(VISIT_COUNT_KEY)) || 0;
  let startedNewSession = false;

  if (!sessionId || expired) {
    sessionId = randomId();
    sessionStartedAt = now;
    visitCount += 1;
    startedNewSession = true;
    writeStore(SESSION_KEY, sessionId);
    writeStore(SESSION_STARTED_AT_KEY, String(now));
    writeStore(VISIT_COUNT_KEY, String(visitCount));
  }

  writeStore(SESSION_LAST_SEEN_KEY, String(now));

  identityCache = {
    visitorId,
    sessionId,
    sessionStartedAt,
    firstSeenAt,
    visitCount,
    isNewVisitor,
    startedNewSession
  };

  return identityCache;
}

function getIdentity() {
  if (!identityCache) {
    return resolveIdentity();
  }

  const now = Date.now();
  const lastSeenAt = Number(readStore(SESSION_LAST_SEEN_KEY)) || 0;

  /* Session went stale while the tab sat open — roll a new one. */
  if (lastSeenAt && now - lastSeenAt > SESSION_TIMEOUT_MS) {
    return resolveIdentity();
  }

  writeStore(SESSION_LAST_SEEN_KEY, String(now));
  return identityCache;
}

/** Keep the current session alive. Called by the auto-tracker on activity. */
export function touchSession() {
  if (typeof window === "undefined") return;
  getIdentity();
}

/** Retention context attached to every event. */
function retentionParams(identity) {
  const now = Date.now();
  return {
    visitor_id: identity.visitorId,
    session_id: identity.sessionId,
    session_age_seconds: Math.floor((now - identity.sessionStartedAt) / 1000),
    visit_count: identity.visitCount,
    is_returning: identity.visitCount > 1,
    days_since_first_visit: identity.firstSeenAt
      ? Math.floor((now - identity.firstSeenAt) / DAY_MS)
      : 0
  };
}

/* --------------------------------------------------------------- instance -- */
async function getAnalyticsInstance() {
  if (analyticsInstance) {
    return analyticsInstance;
  }

  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => resolve(null), ANALYTICS_READY_TIMEOUT_MS);
  });

  analyticsInstance = await Promise.race([analyticsReadyPromise, timeoutPromise]);
  return analyticsInstance;
}

function sanitizeEventParams(params = {}) {
  const sanitized = {};
  let count = 0;

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (count >= MAX_PARAMS) {
      return;
    }

    if (typeof value === "string") {
      sanitized[key] = value.slice(0, MAX_PARAM_VALUE_LENGTH);
    } else if (typeof value === "number" || typeof value === "boolean") {
      sanitized[key] = value;
    } else {
      sanitized[key] = String(value).slice(0, MAX_PARAM_VALUE_LENGTH);
    }
    count += 1;
  });

  return sanitized;
}

/* Debug mode: run `localStorage.appinfigo_analytics_debug = "1"` in the console
   (or set window.__ANALYTICS_DEBUG__) and every event is logged to the console,
   with its full payload, whether or not Firebase is configured. That makes the
   instrumentation verifiable in local dev — where there are no Firebase env
   keys — instead of silently doing nothing. */
function isDebug() {
  if (typeof window === "undefined") return false;
  if (window.__ANALYTICS_DEBUG__) return true;
  return readStore("appinfigo_analytics_debug") === "1";
}

export async function trackEvent(eventName, params = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const identity = getIdentity();
  const payload = sanitizeEventParams({
    ...retentionParams(identity),
    ...params
  });

  if (isDebug()) {
    /* eslint-disable-next-line no-console */
    console.debug("[analytics]", eventName, payload);
    if (Array.isArray(window.__ANALYTICS_LOG__)) {
      window.__ANALYTICS_LOG__.push({ event: eventName, params: payload });
    }
  }

  const analytics = await getAnalyticsInstance();
  if (!analytics) {
    return;
  }

  logEvent(analytics, eventName, payload);
}

/* ------------------------------------------------------------- page views -- */
export async function trackPageView(pathname, title) {
  const identity = getIdentity();

  /* A brand-new session gets one explicit marker, so acquisition and retention
     can be measured without inferring session boundaries after the fact. */
  if (identity.startedNewSession) {
    identity.startedNewSession = false;
    await trackEvent("session_started", {
      entry_path: pathname,
      is_first_ever_visit: identity.visitCount === 1,
      referrer: document.referrer || "direct"
    });
  }

  await trackEvent("page_view", {
    page_path: pathname,
    page_title: title || "Appinfigo",
    page_location: window.location.href
  });
}

export async function trackNavigation(label, destination, source) {
  await trackEvent("navigation_click", {
    nav_label: label,
    nav_destination: destination,
    nav_source: source
  });
}

export async function trackCtaClick(label, section, destination) {
  await trackEvent("cta_click", {
    cta_label: label,
    cta_section: section,
    cta_destination: destination
  });
}

export async function trackFormEvent(action, metadata = {}) {
  await trackEvent("contact_form_event", {
    form_action: action,
    ...metadata
  });
}

/* ------------------------------------------------------- intent + contact --
   The highest-value events on the site: someone trying to reach a human. */

/**
 * A direct contact attempt.
 * @param {"whatsapp"|"call"|"email"} channel
 * @param {string} source  where on the page it was clicked (e.g. "contact_dock")
 * @param {object} [meta]  extra context (message key, page path, ...)
 */
export async function trackContactChannel(channel, source, meta = {}) {
  await trackEvent("contact_channel_click", {
    contact_channel: channel,
    contact_source: source,
    page_path: window.location.pathname,
    ...meta
  });
}

/** A click out to a client project / external site. */
export async function trackOutboundClick(url, label, source) {
  let host = "";
  try {
    host = new URL(url, window.location.origin).hostname.replace(/^www\./, "");
  } catch (error) {
    host = String(url).slice(0, MAX_PARAM_VALUE_LENGTH);
  }

  await trackEvent("outbound_click", {
    outbound_host: host,
    outbound_label: label,
    outbound_source: source,
    page_path: window.location.pathname
  });
}

/**
 * A generic interest signal — an accordion opened, a tab switched, a slide
 * chosen. These are what separate a bounce from genuine evaluation.
 * @param {string} type    e.g. "faq_open", "industry_tab", "showcase_slide"
 * @param {string} label   which item
 * @param {object} [meta]
 */
export async function trackInteraction(type, label, meta = {}) {
  await trackEvent("content_interaction", {
    interaction_type: type,
    interaction_label: label,
    page_path: window.location.pathname,
    ...meta
  });
}

/** A section scrolled into view — which parts of the page actually get seen. */
export async function trackSectionView(sectionId, index) {
  await trackEvent("section_view", {
    section_id: sectionId,
    section_index: index,
    page_path: window.location.pathname
  });
}

/** Scroll-depth milestone (25/50/75/90/100), fired once per page view. */
export async function trackScrollDepth(percent) {
  await trackEvent("scroll_depth", {
    depth_percent: percent,
    page_path: window.location.pathname
  });
}

/**
 * Engaged time — foreground, non-idle seconds only, so it measures attention
 * rather than abandoned tabs. Fired at milestones and once on page hide.
 */
export async function trackEngagement(seconds, reason, meta = {}) {
  await trackEvent("engagement_time", {
    engaged_seconds: seconds,
    engagement_reason: reason,
    page_path: window.location.pathname,
    ...meta
  });
}

/* ------------------------------------------------------------ user props -- */
export async function setAnalyticsUserContext(properties = {}) {
  const analytics = await getAnalyticsInstance();
  if (!analytics) {
    return;
  }

  const identity = getIdentity();

  setUserProperties(
    analytics,
    sanitizeEventParams({
      /* Segmenting properties, so every report can split new vs returning
         without recomputing it from events. */
      visitor_type: identity.visitCount > 1 ? "returning" : "new",
      visit_count_bucket: bucketVisitCount(identity.visitCount),
      ...properties
    })
  );
}

function bucketVisitCount(count) {
  if (count <= 1) return "1";
  if (count <= 3) return "2-3";
  if (count <= 9) return "4-9";
  return "10+";
}

/* ----------------------------------------------------------- performance -- */
export async function trackInitialPerformance() {
  if (
    typeof window === "undefined" ||
    !window.performance ||
    !window.performance.getEntriesByType
  ) {
    return;
  }

  const navigationEntry = window.performance.getEntriesByType("navigation")[0];
  if (!navigationEntry) {
    return;
  }

  await trackEvent("site_performance", {
    dns_ms: Math.round(navigationEntry.domainLookupEnd - navigationEntry.domainLookupStart),
    ttfb_ms: Math.round(navigationEntry.responseStart - navigationEntry.requestStart),
    dom_interactive_ms: Math.round(navigationEntry.domInteractive),
    load_event_ms: Math.round(navigationEntry.loadEventEnd),
    transfer_size_kb: Math.round((navigationEntry.transferSize || 0) / 1024)
  });
}
